import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { authExchange, errMessage } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

export const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser, login } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams((location.hash || "").replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }

    (async () => {
      try {
        const data = await authExchange(sessionId);
        setUser(data.user);
        toast.success(`Signed in as ${data.user?.email}`, {
          description: data.bootstrapped
            ? "You are the first user, so you now have full admin access."
            : data.user?.role === "admin"
              ? "Admin access: settings, exports and the activity log."
              : "Read-only access to the inbox and queue.",
        });
        navigate("/", { replace: true });
      } catch (e) {
        setError(errMessage(e, "Sign-in failed. Please try again."));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="vde-card w-full max-w-md p-6" data-testid="auth-callback-error">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-danger-surface">
            <AlertTriangle className="h-5 w-5 text-danger-text" aria-hidden="true" />
          </span>
          <h1 className="mt-3 text-base font-semibold tracking-tight">Could not complete sign-in</h1>
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{error}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={login} className="h-9" data-testid="auth-callback-retry">
              Try again
            </Button>
            <Button
              variant="outline"
              className="h-9 bg-card"
              onClick={() => navigate("/", { replace: true })}
              data-testid="auth-callback-back"
            >
              Back to sign-in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-4"
      data-testid="auth-callback"
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
        <FileSpreadsheet className="h-5 w-5" aria-hidden="true" />
      </span>
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Finishing sign-in…
      </p>
    </div>
  );
};
